import {
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Table,
  Tooltip,
  Form,
  notification,
  PageHeader,
  Switch,
  Row,
  Col,
} from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconNotFound from "../../static/icon/IconNotFound";
import DeleteAction from "../../Tools/DeleteAction";

import { useAuth } from "../../context/auth";
import PageDropdown from "../Dropdown/PageDropdown";
import IconTutorials from "../../static/icon/IconTutorials";

const GuideTable = () => {
  const history = useHistory();
  const { accessToken } = useAuth();

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState([]);
  const [search, setSearch] = useState({
    pageValue: history.location.pageValue ?? {},
    searchValue: history.location.searchValue ?? {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [currentPage, setCurrentPage] = useState(null);

  useEffect(() => {
    async function getPages({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/page', window.location);
      url.searchParams.append('type', 'guide');
      if (searchValue.id) url.searchParams.append('id', searchValue.id);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);
      if (pageValue.sort) url.searchParams.append('sort', pageValue.sort);
      if (pageValue.order) url.searchParams.append('order', pageValue.order);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, pages } = await res.json();
        setTotal(total);
        setPages(pages);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getPages(search);
  }, [search, accessToken, update]);

  const columns = [
    {
      title: "Tutorial Title (EN)",
      dataIndex: "titleEn",
      key: "titleTh",
      sorter: () => null,
      render: (titleEn) => {
        return (
          <Tooltip title={titleEn}>
            <div className="ellipsis-two w-76">{titleEn}</div>
          </Tooltip>
        );
      },
    },
    {
      title: "Tutorial Title (TH)",
      dataIndex: "titleTh",
      key: "titleTh",
      sorter: () => null,
      render: (titleTh) => {
        return (
          <Tooltip title={titleTh}>
            <div className="ellipsis-two w-76">{titleTh}</div>
          </Tooltip>
        );
      },
    },
    {
      title: "Inactive/Active",
      dataIndex: "active",
      key: "active",
      width: "10%",
      align: "center",
      sorter: () => null,
      render: (active, page) => (
        <Switch
          checked={active}
          onChange={(active) => {
            updatePageActive(active, page);
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: "10%",
      render: (_, page) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tooltip placement="bottom" title="Edit" className="mx-1">
            <EditTwoTone
              style={{
                backgroundColor: "#F3F6F9",
                padding: 6,
                borderRadius: 6,
              }}
              onClick={() => {
                history.push({
                  pathname: `/guide/${page.id}`,
                  searchValue: search.searchValue,
                  pageValue: search.pageValue,
                });
              }}
            />
          </Tooltip>

          {!page.active && (
            <Tooltip placement="bottom" title="Delete" className="mx-1">
              <DeleteTwoTone
                style={{
                  backgroundColor: "#F3F6F9",
                  padding: 6,
                  borderRadius: 6,
                }}
                onClick={() => setCurrentPage(page)}
                twoToneColor="red"
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  async function updatePageActive(active, page) {
    const url = new URL('/api/page/' + page.id, window.location);

    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        'X-ACCESS-TOKEN': accessToken,
        'Content-type': 'application/json',
      },
      body: JSON.stringify({ active }),
    });
    if (res.ok) {
      notification.success({
        message: "Success",
        description: "Successfully saved data.",
      });
    } else {
      notification["error"]({
        message: "Something went wrong !",
        description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
      });
    }

    setUpdate(update + 1);
  };

  return (
    <>
      <div className="flex justify-between items-center mt-2">
        <PageHeader
          className="p-0 w-full"
          title={
            <span className="flex items-center mt-2">
              <IconTutorials /> &nbsp;&nbsp;Create Tutorials
            </span>
          }
        />

        <Button
          className="mt-2 flex items-center"
          icon={<PlusOutlined />}
          type="primary"
          shape="round"
          onClick={() =>
            history.push({
              pathname: "/guide/new",
              searchValue: search.searchValue,
              pageValue: search.pageValue,
            })
          }
        >
          New Tutorial
        </Button>
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={8}>
                <Form.Item
                  label="Tutorial Title"
                  name="id"
                  colon={false}
                  initialValue={null}
                >
                  <PageDropdown
                    type="guide"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={20}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>
                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(page) => page.id}
          loading={loading}
          columns={columns}
          dataSource={pages}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <DeleteAction
        openModalActionDelete={!!currentPage}
        name="Tutorial"
        type="page"
        editRecord={currentPage}
        onCancel={() => setCurrentPage(null)}
        onOk={() => {
          setCurrentPage(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default GuideTable;
